/* eslint-disable ember/no-mixins, no-unused-vars */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import ReportingBaseMixin from 'linced-app/mixins/reporting-base';

export default class ViewController extends Controller.extend(
  ReportingBaseMixin,
) {
  @service
  router;

  @service
  session;

  @service
  store;

  @service
  studentReportActions;

  @service
  toaster;

  StudentReportState = StudentReportState;
  Scopes = Scopes;
  person = null;
  template = null;
  studentReport = null;
  // Set by the route from the student controller, which knows the template's
  // current version. False hides Void and edit: a deleted template takes no
  // new reports, and one whose current version couldn't be resolved might be
  // deleted.
  @tracked
  canVoidRecreate = false;
  @tracked
  isVoiding = false;
  @tracked
  isPublishing = false;
  @tracked
  voidConfirmOpen = false;
  @tracked
  voidRecreateConfirmOpen = false;

  // Registered by the on-screen ReportView so publishReport can (re)capture the
  // report's strand/progress visuals onto its sections before publishing.
  @tracked
  _captureReportVisuals = null;

  @tracked
  _verifyCapturedVisuals = null;
  @tracked
  _waitForDataReady = null;

  // Capture the live visuals and confirm they serialised, retrying the settle +
  // capture once. Returns true when it's safe to publish. The report is
  // rendered on-screen here (RTP → live charts), so we capture from that
  // instance directly rather than rendering off-screen. Mirrors the
  // PublishReportsRunner contract: a render miss or a save failure means we do
  // NOT publish, so the snapshot is never frozen with a blank/unacknowledged
  // visual (there is no backend backstop — see docs/reporting/publish-snapshot.md).
  async _captureVisualsForPublish() {
    if (!this._captureReportVisuals) {
      // No capture hook registered (ReportView failed to mount) — don't publish
      // a report whose visuals we couldn't snapshot.
      return false;
    }
    try {
      await this._captureReportVisuals();
    } catch (e) {
      return false;
    }
    let missing = this._verifyCapturedVisuals
      ? this._verifyCapturedVisuals()
      : [];
    if (missing.length) {
      if (this._waitForDataReady) {
        await this._waitForDataReady();
      }
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );
      try {
        await this._captureReportVisuals();
      } catch (e) {
        // A retry save failure is definitive: captureVisuals sets the
        // serialised value on the in-memory model before awaiting save, so a
        // recheck would falsely pass. Don't publish.
        return false;
      }
      missing = this._verifyCapturedVisuals
        ? this._verifyCapturedVisuals()
        : [];
    }
    return missing.length === 0;
  }

  @action
  exitView() {
    this.router.transitionTo('reporting.group.student');
  }

  @action
  registerReportViewCapture(fn) {
    this._captureReportVisuals = fn;
  }

  @action
  registerReportViewVerify(fn) {
    this._verifyCapturedVisuals = fn;
  }

  @action
  registerReportViewWaitForReady(fn) {
    this._waitForDataReady = fn;
  }

  @action
  async publishReport() {
    this.isPublishing = true;
    try {
      // Capture + verify the live visuals onto the sections before the
      // publish freezes them into the immutable snapshot.
      let captured = await this._captureVisualsForPublish();
      if (!captured) {
        this.toaster.error(
          'Some report visuals failed to render — report not published. Please try again.',
        );
        return;
      }
      await this.studentReportActions.publish(this.studentReport);
      this.router.transitionTo('reporting.group.student');
    } catch (e) {
      this.toaster.error('Failed to publish report');
    } finally {
      this.isPublishing = false;
    }
  }

  @action
  voidReport() {
    this.voidConfirmOpen = true;
  }

  @action
  closeVoidConfirm() {
    if (this.isVoiding) return;
    this.voidConfirmOpen = false;
  }

  @action
  async confirmVoid() {
    this.isVoiding = true;
    try {
      await this.studentReportActions.void(this.studentReport);
      this.voidConfirmOpen = false;
      this.router.transitionTo('reporting.group.student');
    } catch (e) {
      this.toaster.error('Failed to void report');
    } finally {
      this.isVoiding = false;
    }
  }

  @action
  openVoidRecreate() {
    if (!this.canVoidRecreate) return;
    this.voidRecreateConfirmOpen = true;
  }

  @action
  closeVoidRecreate() {
    if (this.isVoiding) return;
    this.voidRecreateConfirmOpen = false;
  }

  // Void the published report and recreate it as a fresh draft. The recreate
  // endpoint requires the report to be voided first, and copies the content
  // against the CURRENT (latest) template version — so if the template has
  // been updated since publish the new draft uses the latest version. Lands
  // the user on the new draft's write view.
  //
  // recreate returns 409 when a report for the same school/student/period/year
  // already exists on the latest template. Because the void must happen first,
  // a 409 leaves the report voided with no new copy — so we surface that
  // clearly and return to the student's report list rather than the (now
  // voided) report view.
  @action
  async confirmVoidRecreate() {
    this.isVoiding = true;
    let person = this.person;
    let voided = false;
    try {
      let oldReport = this.studentReport;

      await this.studentReportActions.void(oldReport);
      voided = true;
      let draft = await this.studentReportActions.recreate(oldReport);

      let studentCtrl = this.controllerFor('reporting.group.student');
      await this.studentReportActions.refreshStudentReports(
        studentCtrl,
        person.get('id'),
      );

      this.router.transitionTo(
        'reporting.group.student.write',
        draft.get('id'),
      );
    } catch (e) {
      let isConflict =
        voided &&
        e &&
        (e.status === 409 || (e.message && e.message.includes('409')));
      this.toaster.error(
        isConflict
          ? 'This report was voided, but a report for this student already exists on the latest template for this period — a new copy was not created.'
          : voided
            ? 'The report was voided, but creating the new copy failed. Please try again.'
            : 'Failed to void the report. Please try again.',
      );
      let studentCtrl = this.controllerFor('reporting.group.student');
      await this.studentReportActions
        .refreshStudentReports(studentCtrl, person.get('id'))
        .catch(() => null);
      // The report is now voided — leave the (now invalid) report view for
      // the list so the user sees the current state.
      if (voided) {
        this.router.transitionTo('reporting.group.student');
      }
    } finally {
      this.isVoiding = false;
      this.voidRecreateConfirmOpen = false;
    }
  }
}
